import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Button } from 'src/components/design-system';
import { Tag } from 'antd';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getLang } from 'shared/store/selectors/lang.selector';
import classNames from 'classnames';
import styles from './table-active-filters.module.scss';

const BLANKS_SENTINEL = '__blanks__';
const SIMPLE_FILTER_SENTINEL = '__simple__';

// AG Grid's built-in text/number/date filter model: { filterType, type, filter | dateFrom, ... }.
const SIMPLE_FILTER_OPS = {
  equals: '=',
  notEqual: '≠',
  lessThan: '<',
  lessThanOrEqual: '≤',
  greaterThan: '>',
  greaterThanOrEqual: '≥',
};

const isSimpleFilterModel = model =>
  !!model &&
  typeof model.type === 'string' &&
  ['text', 'number', 'date'].includes(model.filterType);

const simpleFilterLabel = (model, format) => {
  const op = SIMPLE_FILTER_OPS[model.type] ?? '';
  const isDate = model.filterType === 'date';
  const from = isDate ? model.dateFrom : model.filter;
  const to = isDate ? model.dateTo : model.filterTo;
  const fmt = v => (v == null || v === '' ? '' : format(isDate ? String(v).split(' ')[0] : v));
  const value = model.type === 'inRange' ? `${fmt(from)} – ${fmt(to)}` : fmt(from);
  return `${op} ${value}`.trim();
};

/**
 * TableActiveFilters - Displays active filter tags with clear functionality
 * Works in two modes:
 * 1. Client-side (automatic): Uses AG Grid API to read/write filter state
 * 2. Server-side (controlled): Uses provided filterValues and callbacks
 *
 * @param {Object} props
 * @param {import('ag-grid-community').GridApi} props.api - AG Grid API instance
 * @param {Object} [props.activeFiltersProps] - Props for controlled mode
 * @param {Object} [props.activeFiltersProps.filterValues] - Current filter values keyed by colId
 * @param {Function} [props.activeFiltersProps.onFilterChange] - Called with (colId, newValue) when filter removed
 * @param {Function} [props.activeFiltersProps.onClearAll] - Called when "Clear all" clicked
 * @param {Object} [props.activeFiltersProps.staticFilters] - Filters that cannot be removed.
 *   Keys are colIds, values are arrays of { value, label }. Rendered without close button.
 * @param {string} [props.className] - Additional class name for the container
 */
export function TableActiveFilters({ api, activeFiltersProps, className }) {
  const lang = useSelector(getLang('FILTERS'));
  const [internalFilterModel, setInternalFilterModel] = useState({});

  // Determine if we're in controlled mode
  const isControlled = !!activeFiltersProps?.filterValues;
  const staticFilters = useMemo(
    () => activeFiltersProps?.staticFilters || {},
    [activeFiltersProps?.staticFilters],
  );
  const staticFilterKeys = useMemo(() => new Set(Object.keys(staticFilters)), [staticFilters]);

  // Listen to AG Grid filter changes for client-side mode
  useEffect(() => {
    if (!api || isControlled) return;

    const onFilterChanged = () => {
      setInternalFilterModel(api.getFilterModel() || {});
    };

    api.addEventListener('filterChanged', onFilterChanged);
    // Get initial state
    setInternalFilterModel(api.getFilterModel() || {});

    return () => {
      api.removeEventListener('filterChanged', onFilterChanged);
    };
  }, [api, isControlled]);

  // Get filter model from either controlled props or internal state
  const filterModel = isControlled ? activeFiltersProps.filterValues : internalFilterModel;

  // Get column definitions from AG Grid API for display info (memoized)
  const columnsInfo = useMemo(() => {
    if (!api) return {};
    const info = {};
    const allColumns = api.getColumns() || [];

    allColumns.forEach(col => {
      if (!col) return;
      const colDef = col.getColDef?.() || {};
      const colId = col.getColId?.();
      if (!colId) return;
      info[colId] = {
        headerName: colDef.headerName || colId,
        filterParams: colDef.filterParams || {},
      };
    });

    return info;
  }, [api]);

  // Clear a single filter value
  const handleRemove = useCallback(
    (colId, valueToRemove) => {
      if (isControlled) {
        // Controlled mode: call the provided callback
        const currentValues = filterModel[colId];
        if (Array.isArray(currentValues)) {
          const newValue = currentValues.filter(v => v !== valueToRemove);
          activeFiltersProps.onFilterChange(colId, newValue.length > 0 ? newValue : undefined);
        } else {
          activeFiltersProps.onFilterChange(colId, undefined);
        }
      } else {
        // Client-side mode: update AG Grid filter model
        const currentModel = api.getFilterModel();
        const colModel = currentModel[colId];

        if (valueToRemove === SIMPLE_FILTER_SENTINEL) {
          // Simple text/number/date filter — clear the whole column.
          api
            .setColumnFilterModel(colId, null)
            .then(() => api.onFilterChanged())
            .catch(err => console.error('Failed to clear filter:', err));
        } else if (valueToRemove === BLANKS_SENTINEL) {
          // Removing blanks filter
          const remainingValues = colModel?.values?.length > 0;
          const newModel = remainingValues ? { ...colModel, filterBlanks: false } : null;
          api
            .setColumnFilterModel(colId, newModel)
            .then(() => api.onFilterChanged())
            .catch(err => console.error('Failed to update filter:', err));
        } else if (colModel?.values) {
          // Multi-select filter (SetFilter)
          const newValues = colModel.values.filter(v => v !== valueToRemove);
          if (newValues.length > 0 || colModel.filterBlanks) {
            api
              .setColumnFilterModel(colId, { ...colModel, values: newValues })
              .then(() => api.onFilterChanged())
              .catch(err => console.error('Failed to update filter:', err));
          } else {
            api
              .setColumnFilterModel(colId, null)
              .then(() => api.onFilterChanged())
              .catch(err => console.error('Failed to clear filter:', err));
          }
        } else {
          // Single value filter (RadioSetFilter)
          api
            .setColumnFilterModel(colId, null)
            .then(() => api.onFilterChanged())
            .catch(err => console.error('Failed to clear filter:', err));
        }
      }
    },
    [api, isControlled, filterModel, activeFiltersProps],
  );

  // Clear all filters
  const handleClearAll = useCallback(() => {
    if (isControlled) {
      activeFiltersProps.onClearAll();
    } else {
      api.setFilterModel(null);
    }
  }, [api, isControlled, activeFiltersProps]);

  // Build static filter tags (non-removable)
  const staticFilterTags = useMemo(() => {
    const tags = [];
    Object.entries(staticFilters).forEach(([colId, filterEntries]) => {
      if (!filterEntries || !Array.isArray(filterEntries)) return;
      const colInfo = columnsInfo[colId] || { headerName: colId };
      const headerLabel = colInfo.headerName;

      filterEntries.forEach((entry, index) => {
        tags.push(
          <Tag
            key={`static-${colId}-${entry.value}-${index}`}
            closable={false}
            className={classNames(styles.filterTag, styles.staticFilterTag)}>
            {headerLabel}: {entry.label} {entry.extra}
          </Tag>,
        );
      });
    });
    return tags;
  }, [staticFilters, columnsInfo]);

  // Build the active filter tags
  const activeFilterTags = useMemo(() => {
    const tags = [];

    Object.entries(filterModel).forEach(([colId, filterValue]) => {
      if (!filterValue) return;
      if (staticFilterKeys.has(colId)) return;

      const colInfo = columnsInfo[colId] || { headerName: colId, filterParams: {} };
      const label = colInfo.headerName;

      // Get display value from filter options or valueFormatter
      const getDisplayValue = val => {
        const { options, valueFormatter } = colInfo.filterParams || {};

        // First try options array (has text/label)
        if (options) {
          const option = options.find(opt => opt.value === val);
          if (option) return option.text || option.label;
        }

        // Then try valueFormatter function
        if (typeof valueFormatter === 'function') {
          const formatted = valueFormatter(val);
          if (formatted) return formatted;
        }

        return String(val);
      };

      // Handle different filter value formats
      let values = [];
      let hasFilterBlanks = false;

      if (Array.isArray(filterValue)) {
        // Controlled mode array: [value1, value2]
        values = filterValue;
      } else if (filterValue.values && Array.isArray(filterValue.values)) {
        // AG Grid SetFilter format: { values: [value1, value2], filterBlanks?: boolean }
        values = filterValue.values;
        hasFilterBlanks = !!filterValue.filterBlanks;
      } else if (filterValue.filterBlanks) {
        // AG Grid SetFilter with only blanks selected
        hasFilterBlanks = true;
      } else if (filterValue.value !== undefined) {
        // AG Grid RadioSetFilter format: { value: singleValue }
        values = [filterValue.value];
      } else if (isSimpleFilterModel(filterValue)) {
        // AG Grid text/number/date filter — one removable chip for the whole column.
        tags.push(
          <Tag
            key={`${colId}-simple`}
            closable
            onClose={() => handleRemove(colId, SIMPLE_FILTER_SENTINEL)}
            className={styles.filterTag}
            closeIcon={
              <button
                className={styles.closeIcon}
                aria-label={lang.REMOVE_FILTER.replace('{label}', label)}>
                <FontAwesomeIcon className={styles.icon} icon={faXmark} size="lg" />
              </button>
            }>
            {label}: {simpleFilterLabel(filterValue, getDisplayValue)}
          </Tag>,
        );
        return;
      } else if (typeof filterValue === 'object') {
        // Other object formats - skip
        return;
      } else {
        // Single primitive value
        values = [filterValue];
      }

      const uniqueValues = [...new Set(values)];
      uniqueValues.forEach((val, index) => {
        tags.push(
          <Tag
            key={`${colId}-${val}-${index}`}
            closable
            onClose={() => handleRemove(colId, val)}
            className={styles.filterTag}
            closeIcon={
              <button
                className={styles.closeIcon}
                aria-label={lang.REMOVE_FILTER.replace('{label}', label)}>
                <FontAwesomeIcon className={styles.icon} icon={faXmark} size="lg" />
              </button>
            }>
            {label}: {getDisplayValue(val)}
          </Tag>,
        );
      });

      if (hasFilterBlanks) {
        tags.push(
          <Tag
            key={`${colId}-blanks`}
            closable
            onClose={() => handleRemove(colId, BLANKS_SENTINEL)}
            className={styles.filterTag}
            closeIcon={
              <button
                className={styles.closeIcon}
                aria-label={lang.REMOVE_FILTER.replace('{label}', label)}>
                <FontAwesomeIcon className={styles.icon} icon={faXmark} size="lg" />
              </button>
            }>
            {label}: {lang.BLANKS || 'Blanks'}
          </Tag>,
        );
      }
    });

    return tags;
  }, [filterModel, columnsInfo, handleRemove, staticFilterKeys, lang.BLANKS]);

  if (!api || (staticFilterTags.length === 0 && activeFilterTags.length === 0)) return null;

  return (
    <div className={classNames(styles.activeFiltersContainer, className)}>
      <label className={styles.activeFiltersLabel}>{lang.ACTIVE_FILTERS}</label>
      <div className={styles.tagsList}>
        {staticFilterTags}
        {activeFilterTags}
      </div>
      {activeFilterTags.length > 0 && (
        <Button
          type="primary"
          ghost
          size="small"
          onClick={handleClearAll}
          className={styles.clearButton}>
          {lang.CLEAR}
        </Button>
      )}
    </div>
  );
}
